'use client';

import { configureCache, GrowthBook, GrowthBookPayload, GrowthBookProvider } from '@growthbook/growthbook-react';
import { autoAttributesPlugin } from '@growthbook/growthbook/plugins';
import { captureException } from '@sortlist-frontend/mlm/standalone';
import {
  getEnvironment,
  isBrowser,
  isEmptyPlainObject,
  useCookie,
  useQueryParams,
  uuidv4,
} from '@sortlist-frontend/utils';
import { useRouter } from 'next/compat/router';
import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo } from 'react';

import { GB_COOKIE_NAME, GROWTHBOOK_PAYLOAD_PATH } from './constants';
import { shouldEvaluateClientSideExperiments } from './growthbook-client';
import { postAbTestEventInternal } from './tracking';
import { Domain, ExperimentEventBody, FeatureFlagSetup, GrowthbookType } from './types';
import { domainToMarket, getDomain, logInit } from './utils';

const FeatureFlagsContext = createContext<FeatureFlagSetup>({} as FeatureFlagSetup);

const useFeatureFlagContextValue = () => {
  const context = useContext(FeatureFlagsContext);
  if (isEmptyPlainObject(context)) {
    throw new Error(`useIsFeatureActive must be used within an FeatureFlagsProvider`);
  }
  return context as FeatureFlagSetup;
};

export const useFeatureFlagConfigOverride = () => {
  return useFeatureFlagContextValue().configOverride;
};

export type ExperimentPayload = GrowthBookPayload;

export type FeatureFlagsProviderProps = FeatureFlagSetup & {
  enableExperiments?: boolean;
  payloadFromServer?: ExperimentPayload;
  gbUuidFromServer?: string;
};

configureCache({
  // Mark cached evaluation as stale only after 5mins - to reduce number of requests to GB
  // Default is 1 minute
  staleTTL: 1000 * 60 * 5,
  // For debugging purposes mostly
  disableCache: process.env.DISABLE_GROWTHBOOK_CACHE === 'true',
});

export const FeatureFlagsProvider = ({
  children,
  configOverride,
  enableExperiments = false,
  payloadFromServer,
  gbUuidFromServer,
}: PropsWithChildren<FeatureFlagsProviderProps>) => {
  const router = useRouter();
  const { cookie: gbUuidCookie, setCookie: setGbUuidCookie } = useCookie(GB_COOKIE_NAME, gbUuidFromServer);
  const {
    urlParamsObject: { featureFlagsDebug },
  } = useQueryParams<{ featureFlagsDebug?: string }>();

  /**
   * If we didn't enable experiments, or if we should not evaluate experiments (on the client side)
   */
  const shouldInitializeGrowthbook =
    enableExperiments && (!isBrowser() || shouldEvaluateClientSideExperiments(window.location.href));

  const gb = useMemo(() => {
    const gbUuid = gbUuidCookie ?? (isBrowser() ? uuidv4() : undefined);
    const gb = new GrowthBook<GrowthbookType>({
      apiHost: process.env.NEXT_PUBLIC_GROWTHBOOK_API_HOST!,
      clientKey: process.env.NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY!,
      enableDevMode: process.env.NODE_ENV === 'development' || featureFlagsDebug === 'true',
      plugins: isBrowser()
        ? [
            autoAttributesPlugin({
              uuid: gbUuid,
              uuidAutoPersist: false,
            }),
          ]
        : undefined,
      attributes: {
        id: gbUuid,
        market: isBrowser() ? domainToMarket(window.location.host as Domain) : undefined,
      },
      trackingCallback: (experimentData, result) => {
        if (gbUuid == null) {
          captureException(`[Growthbook error]: GB UUID is missing`, {
            extra: { experimentData, result },
            tags: { context: isBrowser() ? 'client' : 'server' },
          });
          return;
        }

        postAbTestEventInternal({
          slug: 'experimentAssigned',
          url: window.location.href,
          gbuuid: gbUuid,
          timestamp: new Date().toISOString(),
          experiments: {
            [experimentData.key]: result.key,
          } satisfies ExperimentEventBody,
          context: 'client',
        });
      },
    });

    if (gbUuidCookie == null && isBrowser()) {
      setGbUuidCookie(gbUuid, { domain: getDomain(window.location.host) });
    }

    if (!shouldInitializeGrowthbook) {
      return gb;
    }

    if (payloadFromServer != null) {
      gb.initSync({
        payload: payloadFromServer,
      });
      logInit({ source: 'sync' }, isBrowser() ? 'client' : 'server');
    }

    return gb;
  }, [payloadFromServer]);

  const updateGrowthBookURL = () => {
    gb.setURL(window.location.href);
  };

  useEffect(() => {
    const initAsync = async () => {
      const isProduction = getEnvironment(window.location.href) === 'production';

      const res = await gb.init({
        timeout: 5000,
      });

      if (res.error == null) {
        logInit(res, 'client');
        return;
      }

      // In production, we try to fetch the payload from the KV store manually as a fallback
      if (isProduction) {
        try {
          const rawPayload = await fetch(`${window.location.origin}${GROWTHBOOK_PAYLOAD_PATH}`);

          const res = await gb.init({
            payload: await rawPayload.json(),
          });

          logInit({ source: 'payload', error: res.error }, 'client');
        } catch (e) {
          captureException('[GB]: Failed to fetch growthbook payload', {
            extra: { error: e },
            tags: { context: 'client' },
          });
        }
      }
    };

    if (!gb.ready && shouldInitializeGrowthbook) {
      initAsync();
    }
  }, [gb.ready]);

  useEffect(() => {
    router?.events.on('routeChangeComplete', updateGrowthBookURL);
    return () => router?.events.off('routeChangeComplete', updateGrowthBookURL);
  }, []);

  return (
    <GrowthBookProvider growthbook={gb}>
      <FeatureFlagsContext.Provider value={{ configOverride }}>{children}</FeatureFlagsContext.Provider>
    </GrowthBookProvider>
  );
};
